import axios from 'axios';
import { setUser as sentrySetUser } from '@sentry/browser';

import type { AuthUser } from '@/types/auth';

/**
 * Raw OIDC claims returned by Envoy's `/auth/userinfo` (plain JSON). This is a
 * same-origin, cookie-authenticated endpoint — NOT part of the JSON:API `Api`
 * client, so it is called directly here.
 */
interface UserinfoResponse {
  sub: string;
  email?: string | null;
  name?: string | null;
  gs_uid?: number;
  gs_cid?: number;
  gs_role?: string;
}

/**
 * Fetches the signed-in user's identity from the Envoy session.
 *
 * Returns the mapped `AuthUser` on success (200). A 401 (no session) rejects —
 * `AuthGateway` turns that into a full-page redirect to `/auth/signin`.
 */
export const fetchUserinfo = async (): Promise<AuthUser> => {
  const { data } = await axios.get<UserinfoResponse>('/auth/userinfo', {
    withCredentials: true,
  });

  if (data.email) {
    sentrySetUser({ email: data.email });
  }

  return {
    uid: data.sub,
    email: data.email ?? null,
    displayName: data.name ?? null,
    gsUid: data.gs_uid,
    gsCid: data.gs_cid,
    gsRole: data.gs_role,
  };
};
